import { FC, useEffect, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	MenuItem,
	TextField,
	Typography,
} from '@mui/material';
import { api } from 'services/api';
import { getPgErrorMessage } from 'services/api/paymentGateway/errors';
import {
	TPgAdminInvoice,
	TPgAdminPaymentMethod,
	TPgChargeResult,
} from 'services/api/paymentGateway/types';
import { PgConfirmDialog } from '../common/PgConfirmDialog';
import { PgFields } from '../common/PgFields';
import { useIdempotencyKey } from '../common/helpers';
import { humanize } from '../common/tableHelpers';
import { formatDate, formatMoney } from '../utils';

type TChargeInvoiceDialogProps = {
	open: boolean;
	onClose: () => void;
	/** Payment methods of the customer to choose from; without them the service order is used. */
	methods?: TPgAdminPaymentMethod[];
	initialInvoiceId?: number | null;
	onDone?: (result: TPgChargeResult) => void;
};

/**
 * Manual charge of one invoice (`charge-invoice`, 03 §4.8, `channel=admin`). The invoice is read first,
 * so the operator confirms the exact invoice and amount. The idempotency key lives as long as the
 * dialog is open and the button stays locked after the gateway answered: a double click cannot
 * charge twice.
 */
export const ChargeInvoiceDialog: FC<TChargeInvoiceDialogProps> = ({
	open,
	onClose,
	methods,
	initialInvoiceId,
	onDone,
}) => {
	const [idempotencyKey] = useIdempotencyKey(open);
	const [invoiceText, setInvoiceText] = useState('');
	const [invoice, setInvoice] = useState<TPgAdminInvoice | null>(null);
	const [lookupBusy, setLookupBusy] = useState(false);
	const [lookupError, setLookupError] = useState<string | null>(null);
	const [methodId, setMethodId] = useState<number | ''>('');
	const [ignoreRule, setIgnoreRule] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [result, setResult] = useState<TPgChargeResult | null>(null);

	const lookup = async (invoiceId: number) => {
		setLookupBusy(true);
		setLookupError(null);
		setInvoice(null);
		try {
			setInvoice(await api.paymentGateway.getInvoice(invoiceId));
		} catch (e) {
			setLookupError(getPgErrorMessage(e, 'Invoice not found'));
		} finally {
			setLookupBusy(false);
		}
	};

	useEffect(() => {
		if (!open) return;
		setInvoiceText(initialInvoiceId ? String(initialInvoiceId) : '');
		setInvoice(null);
		setLookupError(null);
		setMethodId('');
		setIgnoreRule(false);
		setBusy(false);
		setError(null);
		setResult(null);
		if (initialInvoiceId) void lookup(initialInvoiceId);
	}, [open, initialInvoiceId]);

	const invoiceId = /^\d+$/.test(invoiceText.trim()) ? Number(invoiceText.trim()) : null;
	const adminRule = invoice?.payability?.admin;
	const blockedByRule = !!adminRule && !adminRule.allowed;
	const activeMethods = (methods ?? []).filter((method) => method.status === 'active');

	const submit = async (reason: string) => {
		if (!invoice) return;
		setBusy(true);
		setError(null);
		try {
			const charged = await api.paymentGateway.chargeInvoice({
				invoiceId: invoice.invoiceId,
				paymentMethodId: methodId === '' ? null : methodId,
				reason,
				ignorePayabilityRule: ignoreRule,
				idempotencyKey,
			});
			setResult(charged);
			onDone?.(charged);
		} catch (e) {
			setError(getPgErrorMessage(e, 'The charge failed'));
		} finally {
			setBusy(false);
		}
	};

	return (
		<PgConfirmDialog
			open={open}
			title="Charge an invoice"
			confirmLabel={result ? 'Done' : 'Charge'}
			confirmColor="error"
			reasonLabel="Reason (shown in the audit log)"
			confirmDisabled={!invoice || !!result || (blockedByRule && !ignoreRule)}
			busy={busy}
			error={error}
			onConfirm={(reason) => void submit(reason)}
			onClose={onClose}
			maxWidth="md"
			description={
				<Typography variant="body2" sx={{ mb: 2 }}>
					Charges the customer&apos;s saved payment method now, ignoring the days of the monthly
					schedule. Money is taken from the customer: check the invoice below.
				</Typography>
			}
		>
			<Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
				<TextField
					label="Invoice ID (EAS)"
					value={invoiceText}
					onChange={(e) => {
						setInvoiceText(e.target.value);
						setInvoice(null);
					}}
					size="small"
					disabled={busy || !!result}
					sx={{ minWidth: 200 }}
				/>
				<Button
					variant="outlined"
					size="small"
					disabled={invoiceId === null || lookupBusy || busy || !!result}
					onClick={() => invoiceId !== null && void lookup(invoiceId)}
					startIcon={lookupBusy ? <CircularProgress size={14} /> : undefined}
				>
					Find invoice
				</Button>
			</Box>
			{lookupError && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{lookupError}
				</Alert>
			)}
			{invoice && (
				<Box sx={{ mb: 2 }}>
					<PgFields
						items={[
							['Invoice', `${invoice.invoiceNumber} (ID ${invoice.invoiceId})`],
							[
								'Client',
								`${invoice.clientNumber}${invoice.clientName ? `, ${invoice.clientName}` : ''}`,
							],
							[
								'Service',
								invoice.service ? invoice.service.name : `${invoice.serviceType} (not mapped)`,
							],
							['Total', formatMoney(invoice.totalEur, 'EUR')],
							['Paid', formatMoney(invoice.paidEur, 'EUR')],
							['Remaining', formatMoney(invoice.remainingEur, 'EUR')],
							['Due date', formatDate(invoice.dueDate)],
							['Payment status', humanize(invoice.paymentStatus)],
						]}
					/>
					{invoice.paymentState?.easSyncBlocked && (
						<Alert severity="warning" sx={{ mt: 1 }}>
							The payment of this invoice is blocked: a previous payment was not applied to EAS.
						</Alert>
					)}
					{blockedByRule && (
						<Alert severity="warning" sx={{ mt: 1 }}>
							Debt transfer rule: {adminRule?.message ?? 'the invoice is not payable by an admin'}
						</Alert>
					)}
				</Box>
			)}
			{!!methods && (
				<TextField
					select
					label="Payment method"
					value={methodId}
					onChange={(e) => setMethodId(e.target.value === '' ? '' : Number(e.target.value))}
					size="small"
					fullWidth
					disabled={busy || !!result}
					sx={{ mb: 1 }}
				>
					<MenuItem value="">Try the methods in the order of the service</MenuItem>
					{activeMethods.map((method) => (
						<MenuItem key={method.id} value={method.id}>
							{method.label}
						</MenuItem>
					))}
				</TextField>
			)}
			<FormControlLabel
				control={
					<Checkbox
						checked={ignoreRule}
						onChange={(e) => setIgnoreRule(e.target.checked)}
						disabled={busy || !!result}
					/>
				}
				label="Ignore the debt transfer rule"
			/>
			{result && (
				<Alert severity={result.isSuccess ? 'success' : 'error'} sx={{ mt: 1 }}>
					{result.message || humanize(result.status)} — {humanize(result.status)},{' '}
					{formatMoney(result.total, result.currency)} (transaction {result.transactionId}).
				</Alert>
			)}
		</PgConfirmDialog>
	);
};
